import { Text, TextArea } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type Control, Controller, useFormState } from "react-hook-form";

import { DISPUTE_REASON, type DisputeReason } from "../../../types/createBanDispute.schema";
import { type BanDisputeFormData } from "../DisputeBanForm";
import { DisputeFileUpload } from "./DisputeFileUpload";

interface DisputeDetailsStepProps {
  reason: DisputeReason;
  control: Control<BanDisputeFormData>;
}

interface StepConfig {
  title: string;
  question: string;
  placeholder: string;
}

const STEP_CONFIGS: Record<DisputeReason, StepConfig> = {
  [DISPUTE_REASON.ATTENDED_INTERVIEW]: {
    title: "We're sorry if we got this wrong.",
    question: "Share some details about the interview to help us verify you attended",
    placeholder: "Who did you interview with? What next steps were discussed?",
  },
  [DISPUTE_REASON.EMERGENCY]: {
    title: "We understand emergencies happen.",
    question: "What happened?",
    placeholder: "Help us understand the full story",
  },
  [DISPUTE_REASON.OTHER]: {
    title: "Please explain what happened.",
    question: "Explanation",
    placeholder: "Tell us what happened...",
  },
};

export function DisputeDetailsStep(props: DisputeDetailsStepProps) {
  const { reason, control } = props;

  const { errors, isValid, isSubmitting } = useFormState({ control });

  const config = STEP_CONFIGS[reason];

  return (
    <Stack spacing={8}>
      <Text>{config.title}</Text>

      <Stack spacing={4}>
        <Text>{config.question}</Text>

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <TextArea
              {...field}
              fullWidth
              rows={6}
              placeholder={config.placeholder}
              error={Boolean(errors.description)}
            />
          )}
        />
      </Stack>

      <Controller
        control={control}
        name="evidenceFile"
        render={({ field }) => (
          <DisputeFileUpload
            selectedFile={field.value}
            onFileSelected={field.onChange}
            onFileRemoved={field.onChange}
          />
        )}
      />

      <LoadingButton
        fullWidth
        type="submit"
        isLoading={isSubmitting}
        variant="contained"
        disabled={!isValid || isSubmitting}
      >
        Submit
      </LoadingButton>
    </Stack>
  );
}
